import { Component, Event, EventEmitter, h, State } from '@stencil/core';
import { createUserWithEmailAndPassword } from '@firebase/auth';
import { httpsCallable } from 'firebase/functions';

import { FirebaseService } from '../../utils/firebase';
import { inputOut } from '../../utils/utils';
import { settings } from '../../store/settings.store';
import { customer } from '../../store/customer.store';
import { customerFields, onCustomerFieldsChange, startCustomerFieldsListener, stopCustomerFieldsListener } from '../../store/customer-fields.store';

import { CustomerCreateRequest, CustomerCreateResponse } from '@artask/tambr-models/models/customer.model';
import { addCustomFieldToForm, getCustomFieldValues, getFormErrors, handleFormFieldChange, renderCustomerFields, renderField } from '../../utils/form';


@Component({
  tag: 'tambr-auth-signup',
  styleUrl: 'tambr-auth-signup.css',
  shadow: false
})
export class TambrAuthSignup {

  @State() errorMessages: string[] = [];
  @Event() tambrChangeLoginForm: EventEmitter<string>;

  form = {
    email: { type: 'email', title: 'Email', value: '', isRequired: true },
    password: { type: 'text', title: 'Password', value: '', isRequired: true },
    repeatPassword: { type: 'text', title: 'Repeat password', value: '', isRequired: true },
    firstName: { type: 'text', title: 'First Name', value: '', isRequired: true },
    lastName: { type: 'text', title: 'Last Name', value: '', isRequired: true },
    phone: { type: 'text', title: 'Phone', value: '', isRequired: true },
    termsConfirm: { type: 'checkbox', title: 'Terms checkbox', value: false, isRequired: false },
    subscribed: { type: 'checkbox', title: 'Subscribed', value: false, isRequired: false }
  }

  componentWillLoad() {
    startCustomerFieldsListener();
    onCustomerFieldsChange( 'customFields', customFields => {
      for ( let customField of customFields ) {
        addCustomFieldToForm( this.form, customField );
      }
    });
  }

  disconnectedCallback() {
    stopCustomerFieldsListener();
  }

  validate() {
    this.errorMessages = getFormErrors( this.form );

    if ( this.form.password.value !== this.form.repeatPassword.value ) {
      this.addErrorMessage( `Passwords don't match` );
    }
    if ( !this.form.termsConfirm.value ) {
      this.addErrorMessage( `You have to agree to the terms and conditions in order to create an account.` );
    }
  }

  addErrorMessage( message: string ) { 
    this.errorMessages = [ ...this.errorMessages, message ]; 
  }

  async handleSubmit(ev: any) {
    ev.preventDefault();
    this.validate();
    if ( this.errorMessages.length ) return;
    customer.signUpProcessLoading = true;
    
    try {
      const credential = await createUserWithEmailAndPassword( FirebaseService.auth, this.form.email.value, this.form.password.value );
      console.log( 'credential', credential );

      if ( credential.user ) {
        const customerData: CustomerCreateRequest = {
          businessId: settings.businessId,
          email: this.form.email.value.trim().toLocaleLowerCase(),
          phone: this.form.phone.value,
          lastName: this.form.lastName.value,
          firstName: this.form.firstName.value,
          subscribed: this.form.subscribed.value
        }

        const customerFieldsValues = getCustomFieldValues( this.form, customerFields.customFields );

        if ( customerFieldsValues.length ) {
          customerData.customFields = customerFieldsValues;
        }

        const createCustomer = httpsCallable
          <CustomerCreateRequest, CustomerCreateResponse>
            ( FirebaseService.functions, 'widget_createCustomer' );
          
        await createCustomer( customerData );
        console.log('createCustomer', customerData);
      }
    } catch ( error ) {
      switch( error.code ) {
        case 'auth/email-already-in-use': this.addErrorMessage( 'Email is already in use.' ); break;
        case 'auth/invalid-email': this.addErrorMessage( 'Email is not valid.' ); break;
        case 'auth/weak-password': this.addErrorMessage( 'Password is too weak.' ); break;
        case 'auth/operation-not-allowed': this.addErrorMessage( 'Operation is not allowed.' ); break;
        default: this.addErrorMessage( error.message );
      }
      customer.signUpProcessLoading = false;
    } 
  }


  render() {
    return (
      <form id='tambr-signup-form' class='tambr-auth-form' onSubmit={ ev => this.handleSubmit(ev) }>

        <div class='sticky-messages'>
          <div class={ 'error message' + (this.errorMessages.length ? ' show' : '') }>
            <ul>{ this.errorMessages.map( ( message: string ) => <li>{ message }</li> ) }</ul> 
          </div>
        </div>

        <div class='tambr-form-grid'>
          { renderField( this.form, { id: 'firstName', title: 'First Name', type: 'text', isRequired: true } ) }
          { renderField( this.form, { id: 'lastName', title: 'Last Name', type: 'text', isRequired: true } ) }
          { renderField( this.form, { id: 'email', title: 'Email', type: 'text', isRequired: true } ) }
          { renderField( this.form, { id: 'phone', title: 'Phone', type: 'text', isRequired: true } ) }

          <div class='tambr-form-item'>
            <input type='password' id='password' name='password' 
              value={ this.form.password.value } 
              onInput={ (event) => handleFormFieldChange( this.form, event, 'password' ) }
              onBlur={ (event) => inputOut( event ) } />
            <label htmlFor='password'>Password</label>
            <div class='password-hint'>Password should contain at least 6 characters</div>
          </div>

          <div class='tambr-form-item'>
            <input type='password' id='repeatPassword' name='repeatPassword' 
              value={ this.form.repeatPassword.value } 
              onInput={ (event) => handleFormFieldChange( this.form, event, 'repeatPassword' ) }
              onBlur={ (event) => inputOut( event ) } />
            <label htmlFor='repeatPassword'>Repeat password</label>
          </div>
        </div>
      
        { renderCustomerFields( this.form ) }

        <div class='checkbox-wrapper'>
          <input type='checkbox' id='terms-checkbox' name='terms-checkbox' 
            checked={ this.form.termsConfirm.value } 
            onInput={ (event) => handleFormFieldChange( this.form, event, 'termsConfirm', true ) } />
          <label htmlFor='terms-checkbox'>I agree with Tambr's <a target='_blank' href='https://tambr.app/terms-and-conditions'>Terms and Conditions</a></label>
        </div>
        
        <div class='checkbox-wrapper'>
          <input type='checkbox' id='subscribe-checkbox' name='subscribe-checkbox' 
            checked={ this.form.subscribed.value } 
            onInput={ (event) => handleFormFieldChange( this.form, event, 'subscribed', true ) } />
          <label htmlFor='subscribe-checkbox'>{`I would like to join ${ settings.businessSettings.businessName } mailing list.`}</label>
        </div>

        <div class='tambr-action-buttons'>
          <button type='submit' disabled={ customer.signUpProcessLoading } 
            class={ 'tambr-button' + (customer.signUpProcessLoading ? ' loading' : '') }>
              <div class='tambr-button-loading'><div>Loading...</div></div>
              <span>Sign up</span>
          </button>

          <div class='register-text'>Already have an account? <a class='tambr-clear-button' 
            onClick={ () => this.tambrChangeLoginForm.emit('login') }>Log In</a>
          </div>
        </div>

      </form>
    );
  }

}